import type {
  MetaDeliveryContract,
  MetaPolicyControl,
  MetaPolicyScope,
} from '@/api/types.gen';

/// Above this many chips a row collapses behind a disclosure. The creative
/// automation row carries every refused Meta control — 80-plus of them — which
/// would otherwise bury every other row on the page.
const ITEM_DISCLOSURE_THRESHOLD = 12;

const CONTROL_LABELS: Record<MetaPolicyControl, string> = {
  enforced: 'Enforced by Shubert',
  default: 'Default value',
  editable: 'Editable',
  derived: 'Derived',
};

function LockIcon() {
  return (
    <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true">
      <rect x="3" y="7" width="10" height="7" rx="2" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <path d="M5.25 7V5.25a2.75 2.75 0 0 1 5.5 0V7" fill="none" stroke="currentColor" strokeWidth="1.4" />
    </svg>
  );
}

export interface MetaDeliveryPolicyProps {
  contract?: MetaDeliveryContract | null;
  scopes?: MetaPolicyScope[];
  variant?: 'inline' | 'review' | 'compact';
  heading?: string;
}

export function MetaDeliveryPolicy({
  contract,
  scopes,
  variant = 'inline',
  heading,
}: MetaDeliveryPolicyProps) {
  if (!contract) return null;
  const selected = scopes?.length
    ? contract.sections.filter((section) => scopes.includes(section.scope))
    : contract.sections;
  if (!selected.length) return null;

  return (
    <section className={`meta-policy meta-policy-${variant}`} aria-label={heading || 'Meta delivery contract'}>
      <div className="meta-policy-head">
        <div>
          <div className="meta-policy-eyebrow"><LockIcon /> Meta delivery contract</div>
          <h4>{heading || (selected.length === 1 ? selected[0]?.title : 'Settings applied at publish')}</h4>
        </div>
        <span className="meta-policy-version">Meta {contract.graph_api_version}</span>
      </div>

      <div className="meta-policy-sections">
        {selected.map((section) => (
          <div className="meta-policy-section" key={section.scope}>
            {(selected.length > 1 || heading) && (
              <div className="meta-policy-section-head">
                <h5>{section.title}</h5>
                <span>{section.summary}</span>
              </div>
            )}
            <div className="meta-policy-rows">
              {section.rows.map((row) => (
                <div
                  className={`meta-policy-row control-${row.control}${row.configured === false ? ' is-missing' : ''}`}
                  key={row.code}
                >
                  <div className="meta-policy-row-main">
                    <div className="meta-policy-label">{row.label}</div>
                    <div className="meta-policy-value">{row.value}</div>
                    {row.detail && <div className="meta-policy-detail">{row.detail}</div>}
                  </div>
                  <span className="meta-policy-control">
                    {row.control === 'enforced' && <LockIcon />}
                    {CONTROL_LABELS[row.control]}
                  </span>
                  {row.items.length > 0 && (
                    row.items.length > ITEM_DISCLOSURE_THRESHOLD ? (
                      // Long lists collapse, but nothing is truncated: the
                      // operator can see every enforced value.
                      <details className="meta-policy-items-disclosure">
                        <summary>
                          Show all {row.items.length} enforced values
                        </summary>
                        <div className="meta-policy-items">
                          {row.items.map((item) => <span key={item}>{item}</span>)}
                        </div>
                      </details>
                    ) : (
                      <div className="meta-policy-items">
                        {row.items.map((item) => <span key={item}>{item}</span>)}
                      </div>
                    )
                  )}
                  {row.technical_value && (
                    <details className="meta-policy-technical">
                      <summary>Technical details</summary>
                      <code>{row.technical_value}</code>
                    </details>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export function MetaDeliveryPolicyLoading() {
  return (
    <div className="meta-policy-loading" role="status">
      Loading enforced Meta delivery settings…
    </div>
  );
}
